import { useApp } from "../../context/AppContext";
import { Instagram, Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import footerLogo from "../../assets/footer-logo.png";
import awardBadge from "../../assets/awards/award-badge.png";

const QUICK_LINKS = [
  { label: "Shop", page: "shop" },
  { label: "About Us", page: "about" },
  { label: "Delivery Info", page: "delivery" },
  { label: "Contact", page: "contact" },
];

export default function Footer() {
  const { navigate } = useApp();

  return (
    <footer
      className="bg-brand-forest-600 text-brand-cream-100 mt-auto"
      style={{
        backgroundColor: "#606f49",
      }}
    >
      {/* Main grid */}
      <div className="max-w-6xl mx-auto px-5 pt-10 md:pt-14 pb-8 grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-10">
        {/* Brand column */}
        <div className="pb-7 md:pb-0 border-b border-brand-forest-500 md:border-b-0">
          <div className="mb-4 flex items-center gap-4">
            <img
              src={footerLogo}
              alt="Happy Day Flowers"
              className="h-16 w-auto"
            />
            <img
              src={awardBadge}
              alt="Quality Business Awards 2026 Winner"
              className="h-14 w-auto"
            />
          </div>
          <p className="font-body text-sm text-brand-cream-300 leading-relaxed mb-5">
            Family-run florist in Travancore, Melbourne. Crafting bespoke
            arrangements with market fresh flowers from local suppliers since
            2017.
          </p>
          <div className="flex gap-3">
            <a
              href="https://www.instagram.com/happydayflowers1?igsh=MmRtNHFvanRmeDF6&utm_source=qr"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="w-9 h-9 rounded-full bg-brand-forest-500 hover:bg-brand-crimson-500 flex items-center justify-center transition-colors duration-200"
            >
              <Instagram className="w-4 h-4 text-brand-cream-100" />
            </a>
            <a
              href="https://www.facebook.com/share/1DxBx9TmSE/?mibextid=wwXIfr"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="w-9 h-9 rounded-full bg-brand-forest-500 hover:bg-brand-crimson-500 flex items-center justify-center transition-colors duration-200"
            >
              <Facebook className="w-4 h-4 text-brand-cream-100" />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="py-7 md:py-0 border-b border-brand-forest-500 md:border-b-0">
          <h3 className="font-body text-[10px] uppercase tracking-[0.25em] text-brand-crimson-200 mb-4 font-semibold">
            Quick Links
          </h3>
          <ul className="space-y-2.5">
            {QUICK_LINKS.map(({ label, page }) => (
              <li key={page}>
                <button
                  onClick={() => navigate(page)}
                  className="font-body text-sm text-brand-cream-200 hover:text-brand-cream-50 hover:translate-x-0.5 transition-all duration-150 flex items-center gap-1.5"
                >
                  <span className="w-1 h-1 rounded-full bg-brand-crimson-300 shrink-0" />
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="pt-7 md:pt-0">
          <h3 className="font-body text-[10px] uppercase tracking-[0.25em] text-brand-crimson-200 mb-4 font-semibold">
            Get in Touch
          </h3>
          <ul className="space-y-3">
            <li>
              <a
                href="https://maps.google.com/?q=204+Mt+Alexander+Road+Travancore+VIC"
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-2.5 font-body text-sm text-brand-cream-200 hover:text-brand-cream-50 transition-colors group"
              >
                <MapPin className="w-4 h-4 text-brand-crimson-300 shrink-0 mt-0.5 group-hover:text-brand-crimson-200" />
                204 Mt Alexander Rd, Travancore VIC 3032
              </a>
            </li>
            <li>
              <a
                href="tel:+61432767401"
                className="flex items-center gap-2.5 font-body text-sm text-brand-cream-200 hover:text-brand-cream-50 transition-colors group"
              >
                <Phone className="w-4 h-4 text-brand-crimson-300 shrink-0 group-hover:text-brand-crimson-200" />
                +61 432 767 401
              </a>
            </li>
            <li>
              <a
                href="mailto:happydayflowers1@gmail.com"
                className="flex items-center gap-2.5 font-body text-sm text-brand-cream-200 hover:text-brand-cream-50 transition-colors group"
              >
                <Mail className="w-4 h-4 text-brand-crimson-300 shrink-0 group-hover:text-brand-crimson-200" />
                happydayflowers1@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-2.5 font-body text-sm text-brand-cream-300">
              <Clock className="w-4 h-4 text-brand-crimson-300 shrink-0" />
              Mon–Fri 8am–6pm · Sat–Sun 8:30am–6:00pm
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-brand-forest-500 mx-5 md:mx-0">
        <div className="max-w-6xl mx-auto px-0 md:px-5 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="font-body text-xs text-brand-cream-400">
            © 2025 Happy Day Flowers. All rights reserved.
          </p>
          <p className="font-body text-xs text-brand-cream-400">
            Made with love by Alish.
          </p>
        </div>
      </div>
    </footer>
  );
}
